import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CitySchemas, type PopulatedCity } from "@workspace/contracts/districts";
import { useState } from "react";
import { orpc } from "@/api/orpc";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableActionBar } from "@/components/data-table/data-table-action-bar";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { CreateCityDialog } from "@/features/city/actions/create";
import { DeleteCity } from "@/features/city/actions/delete";
import { UpdateCityDialog } from "@/features/city/actions/update";
import { useCitiesColumns } from "@/features/city/hooks/use-cities-columns";
import { useDataTable } from "@/hooks/use-data-table";
import { Main } from "@/layout/main";
import { withDashboardPaginationDefaults } from "@/lib/pagination";
import type { DataTableRowAction } from "@/types/data-table";

export const Route = createFileRoute("/_authenticated/cities")({
  validateSearch: (search) => CitySchemas.list.parse(withDashboardPaginationDefaults(search)),
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context: { orpc, queryClient } }) => {
    await queryClient.ensureQueryData(orpc.cities.list.queryOptions({ input: deps }));
    return;
  },
});

function RouteComponent() {
  const search = Route.useSearch();

  const [rowAction, setRowAction] = useState<DataTableRowAction<PopulatedCity> | null>(null);

  const columns = useCitiesColumns({ setRowAction });

  const query = useSuspenseQuery(orpc.cities.list.queryOptions({ input: search }));

  const { table } = useDataTable({
    columns: columns,
    data: query.data.items,
    pageCount: query.data.totalPageNumbers,
    debounceMs: 500,
    getRowId: (originalRow) => originalRow.id,
    enableSorting: false,
  });

  return (
    <Main>
      <DataTable table={table} actionBar={<DataTableActionBar table={table} />}>
        <DataTableToolbar table={table}>
          <CreateCityDialog />
        </DataTableToolbar>
      </DataTable>

      <DeleteCity
        open={rowAction?.variant === "delete"}
        onClose={() => setRowAction(null)}
        city={rowAction?.row.original}
      />

      <UpdateCityDialog
        city={rowAction?.row.original}
        open={rowAction?.variant === "update"}
        onClose={() => setRowAction(null)}
      />
    </Main>
  );
}
